import PropTypes from 'prop-types';
import { ReactElement } from 'react';
import { ContainerProps as MuiContainerProps } from '@mui/material/Container';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { CircularProgress } from '@classity-edtech/ui-base';
import Header from '../../components/SEO/Header';
import Masthead from '../../components/Masthead/Spaces';
import CardCluster from '../../components/CardCluster';
import SectionHeading from '../../components/SectionHeading';
import ContactForm from '../../components/ContactForm';
import Gallery from '../../components/Gallery';
import PricingPlan from '../../components/PricingPlan';
import { FAQs } from '../../components/SEO/FAQs';
import Helmet from '../../components/Helmet';
import { Footer } from '../../components/SEO/Footer';
import { useGetPageContentQuery } from '../../services/seoContent';
import { extractFooterContent, extractHeaderContent } from '../../utils/extractContents';

export interface SpacesProps extends MuiContainerProps {}

export const Spaces = ({ className }: SpacesProps): ReactElement => {
  const { data, isLoading: fetchingContent } = useGetPageContentQuery({
    tag: 'spaces',
    contentType: 'page',
  });

  const extractContent = () => {
    if (!!data) {
      return {
        seo: {
          title: data?.seo?.fields?.titleTag,
          keywords: data?.seo?.fields?.keywords,
          description: data?.seo?.fields?.metaDescription,
          metaRobotsIndex: data?.seo?.fields?.metaRobotsIndex,
        },
      };
    }
  };

  const extractMastheadContent = () => {
    const content = data?.sections?.[0];

    return {
      imgMasthead: content?.fields?.list?.[4]?.fields?.media?.fields?.image?.fields?.file?.url,
      heading: content?.fields?.title,
      paragraph: content?.fields?.description?.content?.[0]?.content?.[0]?.value,
      ctaButtons: content?.fields?.list?.slice(0, 3)?.map((item: any) => ({
        label: item?.fields?.reference?.fields?.label ?? '',
        href: item?.fields?.reference?.fields?.url ?? '',
        variant: item?.fields?.title === 'Pricing' ? 'outlined' : undefined,
      })),
      address: content?.fields?.list?.[3]?.fields?.description?.content?.[0]?.content?.[0]?.value,
    };
  };

  const extractCardClusterContent = () => {
    const content = data?.sections?.[1];

    return {
      heading: content?.fields?.title,
      subheading: content?.fields?.description?.content?.[0]?.content?.[0]?.value,
      cards: content?.fields?.list?.map((item: any) => ({
        icon: item?.fields?.media?.fields?.icon?.fields?.file?.url,
        title: item?.fields?.title,
        description: item?.fields?.description?.content?.[0]?.content?.[0]?.value,
      })),
    };
  };

  const extractGalleryContent = () => {
    const content = data?.sections?.[2];

    return {
      heading: content?.fields?.title,
      items:
        content?.fields?.list?.map((item: any) => ({
          image: item?.fields?.media?.fields?.image?.fields?.file?.url ?? '',
          title: item?.fields?.title ?? '',
          description: item?.fields?.description?.content?.[0]?.content?.[0]?.value ?? '',
          hasRibbon: Boolean(item?.fields?.show ?? false),
        })) ?? [],
    };
  };

  const extractPricingPlanContent = () => {
    const content = data?.sections?.[3];

    return {
      heading: content?.fields?.title,
      subheading: content?.fields?.description?.content?.[0]?.content?.[0]?.value,
      plans:
        content?.fields?.list?.map((item: any) => ({
          title: item?.fields?.title ?? '',
          price: item?.fields?.data?.pricing?.price ?? '',
          period: item?.fields?.data?.pricing?.period ?? '',
          description: item?.fields?.description?.content?.map(
            (block: any) => block?.content?.[0]?.value ?? '',
          ) ?? [item?.fields?.description?.content?.[0]?.content?.[0]?.value ?? ''],
          actions: [
            item?.fields?.media && {
              label: item?.fields?.media?.fields?.title ?? '',
              variant: 'primary',
              icon: 'call',
              href: item?.fields?.media?.fields?.url ?? '',
              onClick: () => {
                const url = item?.fields?.media?.fields?.url;

                if (!url) {
                  return;
                }

                if (url.startsWith('#')) {
                  document
                    .getElementById(url.slice(1))
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  return;
                }

                window.open(url, '_blank', 'noopener,noreferrer');
              },
            },
            item?.fields?.reference && {
              label: item?.fields?.reference?.fields?.label ?? '',
              variant: 'secondary',
              icon: 'chevron',
              href: item?.fields?.reference?.fields?.url ?? '',
              onClick: () => {
                const url = item?.fields?.reference?.fields?.url;

                if (!url) {
                  return;
                }

                if (url.startsWith('#')) {
                  document
                    .getElementById(url.slice(1))
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  return;
                }

                window.open(url, '_self', 'noopener,noreferrer');
              },
            },
          ] as any,
          featured: Boolean(item?.fields?.show ?? false),
        })) ?? [],
    };
  };

  const pricingPlanContent = extractPricingPlanContent();

  const extractContactFormHeading = () => {
    const content = data?.sections?.[4];

    return {
      heading: content?.fields?.title,
      subheading: content?.fields?.description?.content?.[0]?.content?.[0]?.value,
    };
  };

  const extractFaqSection = () => {
    const content = data?.sections?.[5];

    return {
      title: content?.fields?.title ?? '',
      faqs:
        content?.fields?.list?.map((item: any) => ({
          question: item?.fields?.title ?? '',
          answers:
            item?.fields?.description?.content?.map(
              (block: any) => block?.content?.[0]?.value ?? '',
            ) ?? [],
        })) ?? [],
    };
  };

  if (fetchingContent) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '80vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box component='main' sx={{ width: '100%' }}>
      <Helmet
        title={extractContent()?.seo?.title}
        keywords={extractContent()?.seo?.keywords}
        description={extractContent()?.seo?.description}
        metaRobotsIndex={extractContent()?.seo?.metaRobotsIndex}
      />
      <Header {...extractHeaderContent(data)} hideNavigation={true} brand={'spaces'} />
      <Box data-reveal>
        <Masthead
          imgMasthead={extractMastheadContent()?.imgMasthead}
          heading={extractMastheadContent()?.heading}
          paragraph={extractMastheadContent()?.paragraph}
          ctaButtons={extractMastheadContent()?.ctaButtons}
          address={extractMastheadContent()?.address}
        />
      </Box>
      <Box data-reveal>
        <CardCluster
          heading={extractCardClusterContent()?.heading}
          subheading={extractCardClusterContent()?.subheading}
          cards={extractCardClusterContent()?.cards || []}
        />
      </Box>
      <Box sx={{ height: { xs: '12px', md: '96px' } }} />
      <Box data-reveal>
        <Gallery
          heading={extractGalleryContent()?.heading}
          items={extractGalleryContent()?.items || []}
        />
      </Box>
      <Box sx={{ height: { xs: '12px', md: '96px' } }} />
      <Box data-reveal>
        <PricingPlan
          heading={pricingPlanContent.heading}
          subheading={pricingPlanContent.subheading}
          plans={pricingPlanContent.plans.length > 0 ? pricingPlanContent.plans : []}
          id={'pricing'}
        />
      </Box>
      <Box sx={{ height: { xs: '12px', md: '96px' } }} />
      <Box data-reveal>
        <SectionHeading
          heading={extractContactFormHeading()?.heading}
          subheading={extractContactFormHeading()?.subheading}
          id={'contact-form'}
        />
      </Box>
      <Box id='contact' data-reveal>
        <ContactForm />
      </Box>
      <Box sx={{ height: { xs: '12px', md: '96px' } }} />
      <Box data-reveal>
        <FAQs {...extractFaqSection()} />
      </Box>
      <Box sx={{ height: { xs: 0, md: '96px' } }} />
      <Footer {...extractFooterContent(data)} />
    </Box>
  );
};

Spaces.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

Spaces.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(Spaces)(
  ({ theme }) => `
    [data-reveal] {
      opacity: 0;
      transform: translateY(24px);
      animation: spacesReveal 600ms ease forwards;
      animation-delay: 120ms;
    }

    @keyframes spacesReveal {
      from {
        opacity: 0;
        transform: translateY(24px);
      }

      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `,
);
